<template>
  <div
    v-if="!isLoaded"
    class="flex h-full items-center justify-center bg-gradient-to-b from-primary to-primary-700"
  >
    <TheLoadingComponent class="text-white" />
  </div>
  <div
    v-else
    class="h-full bg-gradient-to-b from-primary to-primary-700 dark:from-primary-900 dark:to-primary-700"
  >
    <div class="side-nav-gradient flex h-full overflow-hidden">
      <!-- BEGIN: Side Menu -->
      <div
        class="transition-width @container/menu relative hidden flex-col duration-300 ease-in-out md:flex print:hidden"
        :class="{
          'w-24 lg:w-64': !collapsedMenu,
          'w-24': collapsedMenu
        }"
      >
        <div class="z-10 min-h-0 flex-1 flex-col md:flex">
          <div class="shrink-0 px-5 py-3" style="max-width: 250px">
            <RouterLink
              :to="{ name: 'works' }"
              class="flex items-center justify-center gap-3 @3xs/menu:justify-start"
            >
              <CremaLogo class="h-8 w-8 shrink-0 text-primary-300" />
              <span
                v-if="!collapsedMenu"
                class="hidden truncate text-lg font-bold text-white @3xs/menu:inline-block"
              >
                {{ appTitle }}
              </span>
            </RouterLink>
          </div>
          <nav
            class="flex-1 overflow-x-hidden overflow-y-auto px-5 pb-2 text-white dark:text-white"
          >
            <MainMenu />
          </nav>
          <div class="shrink-0 cursor-pointer py-1 pl-5 @3xs/menu:flex">
            <FontAwesomeIcon
              :icon="menuCollapseIcon"
              size="lg"
              class="text-white"
              @click="toggleCollapsed"
            />
          </div>
          <div
            class="flex shrink-0 justify-center border-t p-4 @3xs/menu:justify-start"
            style="max-width: 250px"
          >
            <InfoUserModalComponent />
          </div>
        </div>
      </div>
      <!-- END: Side Menu -->
      <!-- BEGIN: Content -->
      <div class="bg-surface-base content flex flex-col">
        <TheTopBar @ask-for-refresh="incrementKeyForRefresh" />
        <div class="min-h-0 flex-1 overflow-auto sm:p-6">
          <RouterView :key="keyForRefresh" />
        </div>
      </div>
      <!-- END: Content -->
    </div>
  </div>
  <ToastsContainer />
</template>

<script>
import { connectPusher, disconnectPusher } from '@u/pusherClient'
import { debounce } from '@u/debounce'
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import CremaLogo from './CremaLogo.vue'
import TheLoadingComponent from '@c/TheLoadingComponent.vue'
import MainMenu from './MainMenu.vue'
import ToastsContainer from './ToastsContainer.vue'
import InfoUserModalComponent from '../top-bar/InfoUserModalComponent.vue'
import TheTopBar from '../top-bar/TheTopBar.vue'

export default {
  components: {
    CremaLogo,
    FontAwesomeIcon,
    InfoUserModalComponent,
    MainMenu,
    TheLoadingComponent,
    TheTopBar,
    ToastsContainer
  },
  data() {
    return {
      keyForRefresh: 0,
      isLoaded: false
    }
  },
  computed: {
    ...mapGetters('sideMenu', ['collapsedMenu']),
    menuCollapseIcon() {
      return [
        'fal',
        this.collapsedMenu ? 'chevron-double-right' : 'chevron-double-left'
      ]
    },
    appTitle() {
      return import.meta.env.VITE_APP_NAME
    }
  },
  beforeMount() {
    this.initPusher().then((key) => {
      // The key arrives asynchronously: the layout may be gone already.
      // No key (a local API without Pusher): no live updates.
      if (this.isUnmounted || !key) return
      // A burst of events (bulk changes on the API) must not turn into a
      // burst of requests: one reload per second at most.
      const refreshNotifications = debounce(
        () => this.refreshNotificationCenter(),
        1000
      )
      connectPusher(key, 'main', {
        // The API sends an HTML fragment: only its text is shown, so no
        // server markup (or script) reaches the DOM.
        notification: (payload) => {
          const text = new DOMParser()
            .parseFromString(payload.toastify?.node ?? '', 'text/html')
            .body.textContent.trim()
          if (text) this.$h.toastInfo(text)
        },
        'auth/refreshAuth': () => this.refreshAuth(),
        refreshNotificationCenter: refreshNotifications
      })
    })
  },
  mounted() {
    document.body.classList.remove('error-page', 'login')
    document.body.classList.add('main')
    // The router guard has fetched the user (permissions, settings) before
    // this first page, as FM does on each load.
    this.isLoaded = !!this.$store.getters['auth/getAuth']?.id
    if (!this.isLoaded) {
      this.refreshAuth().then(() => {
        this.isLoaded = true
      })
    }
  },
  unmounted() {
    this.isUnmounted = true
    disconnectPusher()
  },
  methods: {
    ...mapActions('sideMenu', ['toggleCollapsed']),
    ...mapActions({
      refreshAuth: 'auth/refreshAuth',
      initPusher: 'auth/initPusher',
      refreshNotificationCenter: 'notification/fetchNotifications'
    }),
    incrementKeyForRefresh() {
      this.keyForRefresh += 1
    }
  }
}
</script>
